import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Typography, ButtonGroup, Button } from '@mui/material';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import CustomTimeZoneDateRangePicker from 'components/DatePIcker/CustomTimeZoneDateRangePicker';
import type { DownTimeAssetDetails, IdlingEvent } from './types';
import type { WorkOrdersSquare } from 'types/squares.type';
import SearchableSelector from 'components/SearchableSelector';
import type { Asset } from 'types/asset.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  isSelected: string;
  setIsSelected: (val: string) => void;
  startDate: Date;
  endDate: Date;
  setStartDate: any;
  setEndDate: any;
  fetchData: any;
  idlingData: IdlingEvent[];
  downtimeData: DownTimeAssetDetails[];
  workOrders: WorkOrdersSquare[];
  selectedComponent: string;
  handleComponentSelection: (arg: string) => void;
  assetList: Asset[];
  handleAssetSelection: (arg: string) => void;
  selectedAsset: string;
}

const dateRangeList = ['1M', '3M', '6M', 'Rolling 1 Yr', 'All'];

export const AssetSelector = ({
  enableDarkTheme,
  isSelected,
  setIsSelected,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  fetchData,
  workOrders,
  selectedComponent,
  handleComponentSelection,
  assetList,
  selectedAsset,
  handleAssetSelection,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const fontColor = palette.textPrimary;
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  const handleSelect = (id: string) => {
    setIsSelected(id);

    const newEndDate = dayjs().tz(timezoneBasedOnDisplayMode).utc().toDate();
    let newStartDate = new Date();

    switch (id) {
      case 'All':
        newStartDate = dayjs('2020-01-01')
          .tz(timezoneBasedOnDisplayMode)
          .utc()
          .toDate();
        break;

      case 'Rolling 1 Yr':
        newStartDate = dayjs()
          .tz(timezoneBasedOnDisplayMode)
          .subtract(1, 'year')
          .utc()
          .toDate();
        break;

      case '1M':
        newStartDate = dayjs()
          .tz(timezoneBasedOnDisplayMode)
          .subtract(1, 'month')
          .utc()
          .toDate();
        break;

      case '3M':
        newStartDate = dayjs()
          .tz(timezoneBasedOnDisplayMode)
          .subtract(3, 'month')
          .utc()
          .toDate();
        break;

      case '6M':
        newStartDate = dayjs()
          .tz(timezoneBasedOnDisplayMode)
          .subtract(6, 'month')
          .utc()
          .toDate();
        break;

      case 'Custom':
        return;

      default:
        return;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  // const adjustedStartDate = adjustDateForLocalFormatting(
  //   startDate,
  //   timezoneBasedOnDisplayMode
  // );
  // const adjustedEndDate = adjustDateForLocalFormatting(
  //   endDate,
  //   timezoneBasedOnDisplayMode
  // );

  const allowedIds = useMemo(
    () => assetList.map((item) => item.bumperNumber),
    [assetList]
  );

  const componentOptions = useMemo(() => {
    const allowedAssetsWorksOrders = workOrders.filter((item) =>
      allowedIds.includes(item.asset_id)
    );
    const uniqueComponents = new Set<string>();
    allowedAssetsWorksOrders.forEach((item) => {
      uniqueComponents.add(item.components_category);
    });
    return Array.from(uniqueComponents);
  }, [workOrders, allowedIds]);

  return (
    <Box
      sx={{
        display: 'flex',
        marginBottom: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          border: '1px solid #2F445E',
          borderRadius: '4px',
          padding: '10px',
          marginRight: '10px',
        }}
      >
        <Typography
          style={{
            fontSize: '12px',
            marginRight: '10px',
            paddingTop: '7px',
            color: palette.textPrimary,
          }}
        >
          {t('quick_range_10')}
        </Typography>
        <ButtonGroup
          size="small"
          variant="outlined"
          aria-label={t('basic_button_group')}
          sx={{
            marginRight: '10px',
          }}
        >
          {dateRangeList.map((item) => {
            return (
              <Button
                key={item}
                size="small"
                style={{
                  backgroundColor:
                    isSelected === item
                      ? palette.primary
                      : enableDarkTheme
                      ? palette.cartesianGrid
                      : palette.boxBackground,
                  color:
                    isSelected === item ? palette.white : palette.textPrimary,
                  textTransform: 'none',
                }}
                onClick={() => {
                  handleSelect(item);
                }}
              >
                {item}
              </Button>
            );
          })}
        </ButtonGroup>
        {isSelected === 'Custom' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <CustomTimeZoneDateRangePicker
              startDate={startDate}
              endDate={endDate}
              maxDate={dayjs(new Date().getTime())}
              onFinalDateChange={handleDateChange}
            />

            <Button
              sx={{
                color: '#FFFFFF',
                backgroundColor: palette.primary,
                fontSize: '12px',
                textTransform: 'none',
                height: '30px',
                '&:hover': {
                  color: enableDarkTheme ? 'white' : 'black',
                  border: enableDarkTheme
                    ? '1px solid white'
                    : '1px solid black',
                },
              }}
              disabled={false}
              onClick={() => {
                fetchData();
              }}
            >
              {t('apply')}
            </Button>
          </div>
        )}
      </Box>
      <Box
        sx={{
          borderRadius: '4px',
          border: '1px solid #2F445E',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: fontColor,
            marginRight: '5px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {t('asset')}
        </Typography>
        <SearchableSelector
          options={[
            'All',
            ...[...allowedIds]
              .filter((x) => x !== 'All')
              .sort((a, b) => a.localeCompare(b)),
          ]}
          selectedValues={[selectedAsset]}
          onSelectionChange={handleAssetSelection}
          placeholder={t('search_assets')}
          label={t('assets')}
          multiple={false}
          showSelectAll={false}
          disabled={selectedComponent !== 'All'}
        />

        <Typography
          sx={{
            fontSize: '14px',
            color: fontColor,
            marginRight: '5px',
            justifyContent: 'center',
            alignItems: 'center',
            marginLeft: '10px',
          }}
        >
          {t('component')}
        </Typography>
        <SearchableSelector
          options={[
            'All',
            ...[...componentOptions]
              .filter((x) => x !== 'All')
              .sort((a, b) => a.localeCompare(b)),
          ]}
          selectedValues={[selectedComponent]}
          onSelectionChange={handleComponentSelection}
          placeholder={t('search_components')}
          label={t('component')}
          multiple={false}
          showSelectAll={false}
          disabled={selectedAsset !== 'All'}
        />
      </Box>
      {/* <Cards
        enableDarkTheme={enableDarkTheme}
        workOrders={workOrders}
        downtimeData={downtimeData}
        startDate={adjustedStartDate}
        endDate={adjustedEndDate}
        idlingData={idlingData}
      /> */}
    </Box>
  );
};
